// src/components/admin/AdminDiscounts.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const EMPTY = {
  code: '', type: 'percent', value: '', source: 'admin',
  single_use: false, min_order_eur: 0, max_uses: '', expires_at: '', active: true,
};

function CodeForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const autoCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    up('code', Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''));
  };

  return (
    <div style={{
      background: '#0d0d0d', border: '1px solid rgba(76,202,116,.2)',
      borderRadius: 16, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        {/* Code */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="input-label">Code</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input-field"
              value={form.code}
              onChange={e => up('code', e.target.value.toUpperCase())}
              placeholder="WELCOME10"
              style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'monospace' }}
            />
            <button type="button" onClick={autoCode} style={{
              background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.25)',
              borderRadius: 10, padding: '0 14px', color: '#4cca74', cursor: 'pointer',
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem', fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
              Auto
            </button>
          </div>
        </div>

        {/* Type */}
        <div>
          <div className="input-label">Type</div>
          <select className="input-field" value={form.type} onChange={e => up('type', e.target.value)} style={{ appearance: 'none' }}>
            <option value="percent">% Percentage</option>
            <option value="fixed">€ Fixed Amount</option>
            <option value="free_shipping">📦 Free Shipping</option>
          </select>
        </div>

        {/* Value */}
        {form.type !== 'free_shipping' && (
          <div>
            <div className="input-label">{form.type === 'percent' ? 'Discount %' : 'Amount (€)'}</div>
            <input
              className="input-field" type="number" min="0" max={form.type === 'percent' ? 100 : undefined}
              value={form.value} onChange={e => up('value', e.target.value)}
              placeholder={form.type === 'percent' ? '10' : '5.00'}
            />
          </div>
        )}

        {/* Min order */}
        <div>
          <div className="input-label">Min Order (€)</div>
          <input className="input-field" type="number" min="0" value={form.min_order_eur}
            onChange={e => up('min_order_eur', e.target.value)} placeholder="0" />
        </div>

        {/* Max uses */}
        <div>
          <div className="input-label">Max Uses (blank = unlimited)</div>
          <input className="input-field" type="number" min="1" value={form.max_uses}
            onChange={e => up('max_uses', e.target.value)} placeholder="∞" />
        </div>

        {/* Expires */}
        <div>
          <div className="input-label">Expires At (optional)</div>
          <input className="input-field" type="datetime-local" value={form.expires_at}
            onChange={e => up('expires_at', e.target.value)} />
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
          {[
            { key: 'active',     label: 'Active' },
            { key: 'single_use', label: 'Single use' },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form[key]} onChange={e => up(key, e.target.checked)}
                style={{ accentColor: '#4cca74', width: 16, height: 16 }} />
              <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.82rem', color: '#ccc' }}>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={() => onSave(form)} style={{ minHeight: 44 }}>
          Save Code
        </button>
        <button onClick={onCancel} style={{
          background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 25,
          padding: '10px 20px', color: '#555', fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: '.85rem', fontWeight: 700, cursor: 'pointer', minHeight: 44,
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function AdminDiscounts() {
  const { showToast } = useApp();
  const [codes, setCodes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | code object
  const [filter, setFilter]   = useState('all');

  const load = async () => {
    const { data } = await supabase
      .from('discount_codes')
      .select('*')
      .order('created_at', { ascending: false });
    setCodes(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (!form.code) { showToast('Code is required', 'error'); return; }
    const payload = {
      code:          form.code.toUpperCase().trim(),
      type:          form.type,
      value:         form.type === 'free_shipping' ? 0 : parseFloat(form.value) || 0,
      source:        'admin',
      single_use:    form.single_use,
      min_order_eur: parseFloat(form.min_order_eur) || 0,
      max_uses:      form.max_uses ? parseInt(form.max_uses) : null,
      expires_at:    form.expires_at || null,
      active:        form.active,
    };
    let err;
    if (editing === 'new') {
      ({ error: err } = await supabase.from('discount_codes').insert(payload));
    } else {
      ({ error: err } = await supabase.from('discount_codes').update(payload).eq('id', editing.id));
    }
    if (err) { showToast('Save failed: ' + err.message, 'error'); return; }
    showToast('Code saved', 'success');
    setEditing(null);
    load();
  };

  const toggleActive = async (code) => {
    await supabase.from('discount_codes').update({ active: !code.active }).eq('id', code.id);
    load();
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Delete this code?')) return;
    await supabase.from('discount_codes').delete().eq('id', id);
    showToast('Code deleted', 'info');
    load();
  };

  const filtered = filter === 'all' ? codes
    : filter === 'active' ? codes.filter(c => c.active)
    : filter === 'wheel'  ? codes.filter(c => c.source === 'wheel')
    : codes.filter(c => !c.active);

  const TypeBadge = ({ type }) => {
    const map = { percent: '%', fixed: '€', free_shipping: '📦' };
    return (
      <span style={{
        background: 'rgba(76,202,116,.12)', color: '#4cca74',
        fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.65rem',
        fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
        padding: '2px 8px', borderRadius: 10,
      }}>{map[type] || type}</span>
    );
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em' }}>
            Discount Codes
          </h2>
          <p style={{ fontSize: '.8rem', color: '#555' }}>{codes.length} total codes</p>
        </div>
        {!editing && (
          <button className="btn-primary" onClick={() => setEditing('new')} style={{ minHeight: 44 }}>
            + New Code
          </button>
        )}
      </div>

      {editing === 'new' && (
        <CodeForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} />
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { key: 'all',    label: `All (${codes.length})` },
          { key: 'active', label: `Active (${codes.filter(c=>c.active).length})` },
          { key: 'wheel',  label: `Wheel (${codes.filter(c=>c.source==='wheel').length})` },
          { key: 'off',    label: `Inactive (${codes.filter(c=>!c.active).length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '5px 14px', border: 'none', borderRadius: 20,
            background: filter === f.key ? 'rgba(76,202,116,.15)' : '#111',
            color: filter === f.key ? '#4cca74' : '#555',
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.78rem',
            fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer',
          }}>{f.label}</button>
        ))}
      </div>

      {/* Code list */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#444' }}>No codes found</div>
        )}
        {filtered.map(code => (
          <div key={code.id}>
            {editing && editing !== 'new' && editing.id === code.id ? (
              <div style={{ padding: '0 0 0 0' }}>
                <CodeForm initial={{ ...code, max_uses: code.max_uses ?? '', expires_at: code.expires_at ? code.expires_at.slice(0, 16) : '' }} onSave={save} onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto',
                gap: 12, padding: '14px 20px',
                borderBottom: '1px solid rgba(255,255,255,.04)',
                alignItems: 'center',
              }}>
                {/* Info */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '.95rem', fontWeight: 700,
                      color: code.active ? '#f4f4f4' : '#555',
                      letterSpacing: '.06em',
                    }}>{code.code}</span>
                    <TypeBadge type={code.type} />
                    {code.source === 'wheel' && (
                      <span style={{ fontSize: '.65rem', color: '#9b59b6', background: 'rgba(155,89,182,.12)', padding: '2px 8px', borderRadius: 10, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>WHEEL</span>
                    )}
                    {!code.active && (
                      <span style={{ fontSize: '.65rem', color: '#555', background: 'rgba(255,255,255,.05)', padding: '2px 8px', borderRadius: 10, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>INACTIVE</span>
                    )}
                  </div>
                  <div style={{ fontSize: '.78rem', color: '#555', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {code.type !== 'free_shipping' && <span style={{ color: '#4cca74', fontWeight: 700 }}>
                      {code.type === 'percent' ? `${code.value}% off` : `€${code.value} off`}
                    </span>}
                    {code.type === 'free_shipping' && <span style={{ color: '#4a9edd' }}>Free shipping</span>}
                    <span>Used: {code.current_uses ?? code.uses ?? 0}{code.max_uses ? ` / ${code.max_uses}` : ''}</span>
                    {code.min_order_eur > 0 && <span>Min: €{code.min_order_eur}</span>}
                    {code.expires_at && <span>Exp: {new Date(code.expires_at).toLocaleDateString()}</span>}
                    {code.single_use && <span>Single use</span>}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button onClick={() => toggleActive(code)} style={{
                    background: 'none', border: `1px solid ${code.active ? 'rgba(240,165,0,.3)' : 'rgba(76,202,116,.3)'}`,
                    borderRadius: 20, padding: '6px 12px',
                    color: code.active ? '#f0a500' : '#4cca74',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                  }}>
                    {code.active ? 'Disable' : 'Enable'}
                  </button>
                  {code.source === 'admin' && (
                    <button onClick={() => setEditing(code)} style={{
                      background: 'none', border: '1px solid rgba(255,255,255,.1)',
                      borderRadius: 20, padding: '6px 12px', color: '#888',
                      fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                    }}>Edit</button>
                  )}
                  <button onClick={() => deleteCode(code.id)} style={{
                    background: 'none', border: '1px solid rgba(224,80,80,.25)',
                    borderRadius: 20, padding: '6px 12px', color: '#e05050',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                  }}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}